/**
 * Job Details Page
 * View details of a specific job opportunity and submit bids
 */

/* eslint-disable react-hooks/exhaustive-deps */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { marketplaceService } from '../../services/marketplace.service';
import type { JobOpportunity, SubmitBidRequest } from '../../types/marketplace';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { toast } from '@/lib/utils/toast';

export function JobDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [job, setJob] = useState<JobOpportunity | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showBidDialog, setShowBidDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Bid form state
  const [bidData, setBidData] = useState<SubmitBidRequest>({
    jobId: id || '',
    planId: '',
    pricing: {
      basePrice: 0,
      estimatedTotal: 0,
    },
    serviceDescription: '',
    message: '',
  });

  useEffect(() => {
    if (id) {
      loadJob();
    }
  }, [id]);

  const loadJob = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);
      const jobData = await marketplaceService.getOpportunity(id);
      setJob(jobData);
      setBidData((prev) => ({ ...prev, planId: jobData.planId }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load job');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitBid = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setSubmitting(true);
      await marketplaceService.submitBid(bidData);
      setShowBidDialog(false);
      toast.success('Bid submitted successfully!');
      navigate('/provider/marketplace/my-bids');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to submit bid');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <p className="mt-4 text-neutral-600">Loading job details...</p>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="card bg-red-50 border-red-200">
        <p className="text-red-800">{error || 'Job not found'}</p>
        <button onClick={() => navigate('/provider/marketplace')} className="btn-secondary mt-4">
          Back to Marketplace
        </button>
      </div>
    );
  }

  return (
    <ErrorBoundary resetKeys={[id || '']}>
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <button onClick={() => navigate('/provider/marketplace')} className="text-primary-600 hover:text-primary-700 mb-4">
            ← Back to Marketplace
          </button>
          <h1 className="text-3xl font-bold text-neutral-900">{job.projectName}</h1>
          <p className="text-neutral-600 mt-2">{job.projectAddress}</p>
        </div>

        <div className="card mb-6">
          <h2 className="text-xl font-semibold text-neutral-900 mb-4">Job Details</h2>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <dt className="text-sm text-neutral-600">Status</dt>
              <dd className="text-base font-medium text-neutral-900">{job.status}</dd>
            </div>
            <div>
              <dt className="text-sm text-neutral-600">Target Diversion Rate</dt>
              <dd className="text-base font-medium text-neutral-900">{job.targetDiversionRate}%</dd>
            </div>
            <div>
              <dt className="text-sm text-neutral-600">Start Date</dt>
              <dd className="text-base font-medium text-neutral-900">
                {new Date(job.startDate * 1000).toLocaleDateString()}
              </dd>
            </div>
            {job.targetCompletionDate && (
              <div>
                <dt className="text-sm text-neutral-600">Target Completion</dt>
                <dd className="text-base font-medium text-neutral-900">
                  {new Date(job.targetCompletionDate * 1000).toLocaleDateString()}
                </dd>
              </div>
            )}
          </dl>
        </div>

        <div className="flex justify-end">
          <button onClick={() => setShowBidDialog(true)} className="btn-primary">
            Submit Bid
          </button>
        </div>

        {/* Bid submission dialog */}
        {showBidDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 max-w-lg w-full">
              <h3 className="text-xl font-semibold text-neutral-900 mb-4">Submit Bid</h3>
              <form onSubmit={handleSubmitBid} className="space-y-4">
                <div>
                  <label htmlFor="basePrice" className="block text-sm font-medium text-neutral-700 mb-2">
                    Base Price ($)
                  </label>
                  <input
                    id="basePrice"
                    type="number"
                    className="input"
                    required
                    min="0"
                    step="0.01"
                    value={bidData.pricing.basePrice}
                    onChange={(e) =>
                      setBidData({
                        ...bidData,
                        pricing: {
                          ...bidData.pricing,
                          basePrice: parseFloat(e.target.value),
                          estimatedTotal: parseFloat(e.target.value),
                        },
                      })
                    }
                  />
                </div>

                <div>
                  <label htmlFor="serviceDescription" className="block text-sm font-medium text-neutral-700 mb-2">
                    Service Description
                  </label>
                  <textarea
                    id="serviceDescription"
                    className="input"
                    rows={4}
                    required
                    minLength={10}
                    maxLength={1000}
                    value={bidData.serviceDescription}
                    onChange={(e) => setBidData({ ...bidData, serviceDescription: e.target.value })}
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-neutral-700 mb-2">
                    Message (Optional)
                  </label>
                  <textarea
                    id="message"
                    className="input"
                    rows={3}
                    maxLength={500}
                    value={bidData.message}
                    onChange={(e) => setBidData({ ...bidData, message: e.target.value })}
                  />
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowBidDialog(false)}
                    className="btn-secondary"
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary" disabled={submitting}>
                    {submitting ? 'Submitting...' : 'Submit Bid'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
}
